import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createTaxonomy, getTaxonomyDetails, resetTaxonomyDetails, updateTaxonomy,
} from '../../../features';
import useToggle from '../../../hooks/useToggle';
import {
  findNodeById,
  updateNodeById,
  deleteNodeById,
  insertChildNode,
  insertSiblingNode,
} from '../utils/treeUtils';
import { DEFAULT_LEVELS } from '../utils/constants';
import { getComposeNodeId, parseComposedId } from '../utils/helpers';

const useTaxonomyTree = () => {
  const { id: taxonomyId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [taxonomyName, setTaxonomyName] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [featureTargetId, setFeatureTargetId] = useState(null);
  const [dialogSelectedFeatures, setDialogSelectedFeatures] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [levels, setLevels] = useState([]);

  const taxonomyDetails = useSelector((state) => state.taxonomyDetails);
  const [openInfo, toggleInfo] = useToggle(false);

  const handleAdd = useCallback((targetId, levelToAdd) => {
    if (levelToAdd > levels.length) return;
    const newId = Date.now().toString();
    const newNode = {
      id: newId, name: '', level: levelToAdd, children: [],
    };
    const updatedTree = insertChildNode(treeData, targetId, newNode);
    setTreeData(updatedTree);
    setExpanded((prev) => [...new Set([...prev, getComposeNodeId(targetId, levelToAdd - 1)])]);
    setSelectedNodeId(getComposeNodeId(newId, levelToAdd));
    setDirty(true);
  }, [treeData, levels]);

  const handleAddSibling = useCallback((targetId, levelToAdd) => {
    if (levelToAdd > levels.length) return;
    const newId = Date.now().toString();
    const newNode = {
      id: newId, name: '', level: levelToAdd, children: [],
    };
    const updatedTree = insertSiblingNode(treeData, targetId, newNode);
    const composedId = getComposeNodeId(newId, levelToAdd);
    setTreeData(updatedTree);
    setExpanded((prev) => [...new Set([...prev, composedId])]);
    setSelectedNodeId(composedId);
    setDirty(true);
  }, [treeData, levels]);

  const handleDelete = useCallback((id) => {
    const updated = deleteNodeById(treeData, id);
    setTreeData(updated);
    setDirty(true);
  }, [treeData]);

  const handleRename = useCallback((id, name) => {
    const updated = updateNodeById(treeData, id, (node) => ({ ...node, name }));
    setTreeData(updated);
    setDirty(true);
  }, [treeData]);

  const handleSelectFeature = useCallback((id) => {
    const node = findNodeById(treeData, id);
    setFeatureTargetId(id);
    setDialogSelectedFeatures(node?.features || []);
    setFeatureDialogOpen(true);
  }, [treeData]);

  const handleSaveFeature = useCallback((id, selectedFeatures, selectedNodeIdLevel) => {
    const updatedTree = updateNodeById(treeData, id, (node) => ({ ...node, features: selectedFeatures }));
    setTreeData(updatedTree);
    setExpanded((prev) => [...new Set([...prev, getComposeNodeId(id, selectedNodeIdLevel)])]);
    setDirty(true);
  }, [treeData]);

  const handleAction = (action, nodeId, depthOrValue) => {
    switch (action) {
      case 'add':
        handleAdd(nodeId, depthOrValue + 1);
        break;
      case 'add-sibling':
        handleAddSibling(nodeId, depthOrValue);
        break;
      case 'edit':
        handleRename(nodeId, depthOrValue);
        break;
      case 'delete':
        handleDelete(nodeId);
        break;
      case 'select-feature':
        handleSelectFeature(nodeId);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (taxonomyId !== 'new') {
      dispatch(getTaxonomyDetails({ id: taxonomyId }));
    } else {
      dispatch(resetTaxonomyDetails());
      setTreeData([]);
      setTaxonomyName('');
      setLevels(DEFAULT_LEVELS);
    }
  }, [taxonomyId]);

  useEffect(() => {
    if (taxonomyId !== 'new') {
      setTreeData(taxonomyDetails.data.tree || []);
      setLevels(taxonomyDetails.data.levels || []);
      setTaxonomyName(taxonomyDetails.data.name || '');
    }
  }, [taxonomyDetails.data, taxonomyId]);

  const levelMap = useMemo(() => {
    if (levels) {
      return Object.fromEntries(levels.map(({ level, name }) => [level, name]));
    }
    return {};
  }, [levels]);

  const handleTaxonomyNameChange = (e) => {
    setTaxonomyName(e.target.value);
    setDirty(true);
  };

  const handleAddFirstNode = () => {
    const newId = Date.now().toString();
    const newNode = {
      id: newId,
      name: '',
      level: 1,
      children: [],
    };

    setTreeData([newNode]);
    setSelectedNodeId(`${newId}|1`);
    setDirty(true);
  };

  const handleSaveTaxonomy = async () => {
    const data = {
      name: taxonomyName,
      level: levels,
      tree: treeData,
    };

    const action = taxonomyId === 'new'
      ? createTaxonomy(data)
      : updateTaxonomy({ id: taxonomyId, ...data });

    dispatch(action).then((res) => {
      if (res?.payload?.success) {
        setDirty(false);
        navigate('/taxonomy');
      }
    });
  };

  const handleDialogFeatureSave = () => {
    const { level: selectedLevel } = parseComposedId(selectedNodeId);
    handleSaveFeature(featureTargetId, dialogSelectedFeatures, selectedLevel);
    setFeatureDialogOpen(false);
    setFeatureTargetId(null);
    setDialogSelectedFeatures([]);
  };

  return {
    // state
    loading: taxonomyDetails.loading,
    treeData,
    taxonomyName,
    setExpanded,
    setSelectedNodeId,
    setDirty,
    levels,
    setLevels,
    levelMap,
    expanded,
    dirty,
    openInfo,
    toggleInfo,
    selectedNodeId,
    featureDialogOpen,
    setFeatureDialogOpen,
    dialogSelectedFeatures,
    setDialogSelectedFeatures,
    // function
    handleAction,
    handleSaveFeature,
    handleAddFirstNode,
    handleSaveTaxonomy,
    handleTaxonomyNameChange,
    handleDialogFeatureSave,
  };
};

export default useTaxonomyTree;
